import React, { useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";

import "./Buttons.scss";
import Button, { ButtonProps } from "../../../../components/button/Button";
import useThemeService from "../../../../services/theme-service/ThemeService";
import useModal from "../../../../components/dialog/useModal";
import WarningBeforeAction from "../../../../components/data-components/WarningBeforeAction";
import { Trash, Warning } from "../../../../components/svg/svgImageList";
import { lastAccessedOrganizationService } from "../../../../services/organization/LastAccessedOrganizationStorage";
import { translationService } from "../../../../services/translation/TranslationService";
import { MyOrganizationsStorage } from "../../../../services/organization/MyOrganizationsStorage";
import { Dialog } from "../../../../components/dialog/Dialog";

interface DeleteOrganizationButtonProps extends ButtonProps {
  disabled: boolean;
  confirmationAction: (organizationId: number) => void;
  myOrganizationsStorage: MyOrganizationsStorage;
}

export const DeleteOrganizationButton = observer(function ({
  disabled,
  myOrganizationsStorage,
  confirmationAction,
}: DeleteOrganizationButtonProps) {
  const history = useHistory();
  const { themeClass } = useThemeService();
  const { isVisible, toggleModal } = useModal();

  const [confirmOrganizationName, setConfirmOrganizationName] = useState("");
  const invalidOrganization = useMemo(
    () => myOrganizationsStorage.nameOfSelectedItem !== confirmOrganizationName,
    [confirmOrganizationName, myOrganizationsStorage.nameOfSelectedItem]
  );

  const showModal = () => {
    setConfirmOrganizationName("");
    toggleModal();
  };

  const handleConfirmationAction = () => {
    confirmationAction(myOrganizationsStorage.idOfSelectedItem);
    toggleModal();
  };

  const handleNavigateToSubscriptionPlans = async () => {
    if (
      lastAccessedOrganizationService.data.organizationId !==
      myOrganizationsStorage.idOfSelectedItem
    )
      await lastAccessedOrganizationService.updateLastAccessedOrganization(
        myOrganizationsStorage.idOfSelectedItem
      );

    history.push("/subscription/plans");
  };

  return (
    <>
      <Button
        id="delete-organization"
        disabled={disabled}
        onClick={showModal}
        icon={{
          svg: Trash,
          textKey: "general.delete.organization",
          textClass: "actions-header-button-label",
          className: themeClass(["icon-trash"]),
        }}
      />

      <WarningBeforeAction
        isVisible={isVisible && !myOrganizationsStorage.hasPaidSubscriptionOfSelectedItem}
        titleKey="MyOrganizations.delete-organization.confirmation.title"
        titleContext={{ organizationName: myOrganizationsStorage.nameOfSelectedItem }}
        subtitleKey="MyOrganizations.delete-organization.confirmation"
        toggleModal={toggleModal}
        buttonOk={{
          id: "delete-organization-ok",
          textKey: "general.delete.organization",
          disabled: invalidOrganization,
          action: handleConfirmationAction,
        }}
      >
        <p>
          {translationService.translate(
            "MyOrganizations.delete-organization.confirmation.validation"
          )}
        </p>
        <input
          id="delete-organization-name"
          placeholder={translationService.translate(
            "MyOrganizations.delete-organization.confirmation.validation-placeholder"
          )}
          value={confirmOrganizationName}
          onChange={(event) => setConfirmOrganizationName(event.target.value)}
          className={themeClass(["input"])}
        />
      </WarningBeforeAction>

      <Dialog
        isVisible={isVisible && myOrganizationsStorage.hasPaidSubscriptionOfSelectedItem}
        titleKey="MyOrganizations.delete-organization.unable.title"
        titleContext={{ organizationName: myOrganizationsStorage.nameOfSelectedItem }}
        onHide={toggleModal}
        className="paid-subscription-warning-modal"
        iconTitle={{
          svg: Warning,
          width: 30,
          height: 30,
          className: themeClass(["icon-warning"], ["partially-failed"]),
        }}
        buttonOk={{
          id: "delete-organization-ok",
          textKey: "general.ok",
          action: toggleModal,
        }}
      >
        <p>
          {translationService.translate("MyOrganizations.delete-organization.unable-part1")}
          <span className="subscription-plans-link" onClick={handleNavigateToSubscriptionPlans}>
            {translationService.translate("MyOrganizations.delete-organization.unable-part2")}
          </span>
        </p>
      </Dialog>
    </>
  );
});
